// Import directly from `./catalogue` (not `./index`) to avoid a
// circular dependency: `index.ts` re-exports `applyMcpFlagDelta` from
// this module, so importing the catalogue back via `./index` would
// create `index → applyMcpFlagDelta → index`, which is fragile under
// CommonJS/conditional-exports resolution.
import { MCP_FLAG_VALUES, MCP_FLAG_DEPENDENCIES, isMcpFlag, type McpFlag } from './catalogue';

export type UnknownFlagFailure = {
  readonly kind: 'unknown-flag';
  readonly field: 'enable' | 'disable';
  readonly value: unknown;
};

export type MissingParentFailure = {
  readonly kind: 'missing-parent';
  /**
   * Why this orphan is being surfaced now.
   * - `'enable'`  — the user attempted to enable `flag` without first
   *   enabling `requires`.
   * - `'disable'` — the user attempted to disable `requires` while
   *   `flag` (which depends on it) remained.
   * - `'current'` — the orphan is pre-existing in the stored row and
   *   the requested delta does not fix it. Surfaces a malformed row
   *   instead of silently rewriting it back into the database.
   */
  readonly path: 'enable' | 'disable' | 'current';
  readonly flag: McpFlag;
  readonly requires: McpFlag;
};

export type ConflictingFlagFailure = {
  readonly kind: 'conflicting-flag';
  readonly flag: McpFlag;
};

/**
 * Emitted when the stored `current` value is not an array (a JSONB column
 * can legally hold any JSON value, so a malformed historical row may store
 * an object/string/etc.). The validator surfaces this as a clean failure
 * so callers can return a 400 instead of crashing inside the iteration.
 */
export type InvalidCurrentSetFailure = {
  readonly kind: 'invalid-current-set';
  readonly value: unknown;
};

export type McpFlagFailure =
  | UnknownFlagFailure
  | MissingParentFailure
  | ConflictingFlagFailure
  | InvalidCurrentSetFailure;

export type McpFlagDeltaInput = {
  readonly current: unknown;
  readonly enable?: readonly unknown[];
  readonly disable?: readonly unknown[];
};

export type McpFlagDeltaResult =
  | { readonly ok: true; readonly value: readonly unknown[] }
  | { readonly ok: false; readonly failures: readonly McpFlagFailure[] };

function validateInputSet(
  input: readonly unknown[],
  field: 'enable' | 'disable'
): { valid: McpFlag[]; failures: UnknownFlagFailure[] } {
  const valid: McpFlag[] = [];
  const failures: UnknownFlagFailure[] = [];
  for (const value of input) {
    if (isMcpFlag(value)) {
      if (!valid.includes(value)) valid.push(value);
    } else {
      failures.push({ kind: 'unknown-flag', field, value });
    }
  }
  return { valid, failures };
}

/**
 * Apply an `{ enable, disable }` delta to a `current` feature-flag list.
 *
 * - `current` MUST be an array (typically the row's `available_features`
 *   JSONB value). Anything else — `null`, `undefined`, an object, a string,
 *   etc. — is rejected with `invalid-current-set` so callers can return a
 *   clean 400 instead of either crashing inside the iteration or silently
 *   normalizing a malformed row to "empty" and overwriting it. This matches
 *   the contract of `validateMcpFlagSet(input, 'current')`.
 * - Unknown MCP flags in `enable` / `disable` fail with `unknown-flag`.
 * - A flag appearing in both `enable` and `disable` fails with `conflicting-flag`.
 * - The resulting MCP flag set must satisfy every dependency declared in
 *   `MCP_FLAG_DEPENDENCIES`; otherwise a `missing-parent` failure is emitted
 *   for each violation.
 *     - Enabling a flag whose parent is absent yields `path: 'enable'`.
 *     - Disabling a parent whose dependent remains yields `path: 'disable'`,
 *       `flag` set to the orphaned dependent, and `requires` set to the
 *       disabled parent.
 * - Non-MCP values in `current` are preserved verbatim (regardless of type),
 *   appearing before the computed MCP flag set in the returned value. This
 *   keeps the operation lossless for unrelated feature flags already stored
 *   on the row.
 */
export function applyMcpFlagDelta(input: McpFlagDeltaInput): McpFlagDeltaResult {
  if (!Array.isArray(input.current)) {
    return {
      ok: false,
      failures: [{ kind: 'invalid-current-set', value: input.current }],
    };
  }

  const currentInput: readonly unknown[] = input.current;
  const enableInput = input.enable ?? [];
  const disableInput = input.disable ?? [];

  const failures: McpFlagFailure[] = [];

  const enableResult = validateInputSet(enableInput, 'enable');
  failures.push(...enableResult.failures);

  const disableResult = validateInputSet(disableInput, 'disable');
  failures.push(...disableResult.failures);

  const conflicts: McpFlag[] = [];
  for (const flag of enableResult.valid) {
    if (disableResult.valid.includes(flag) && !conflicts.includes(flag)) {
      conflicts.push(flag);
    }
  }
  for (const flag of conflicts) {
    failures.push({ kind: 'conflicting-flag', flag });
  }

  if (failures.length > 0) {
    return { ok: false, failures };
  }

  const nonMcp: unknown[] = [];
  const currentMcp: McpFlag[] = [];
  for (const value of currentInput) {
    if (isMcpFlag(value)) {
      if (!currentMcp.includes(value)) currentMcp.push(value);
    } else {
      nonMcp.push(value);
    }
  }

  const nextMcpSet = new Set<McpFlag>(currentMcp);
  for (const flag of enableResult.valid) nextMcpSet.add(flag);
  for (const flag of disableResult.valid) nextMcpSet.delete(flag);

  const dependencyFailures: MissingParentFailure[] = [];

  // Validate the *full* post-update set, not just the deltas. Iterating
  // every flag in `nextMcpSet` catches:
  //   1. enabling a child without its parent (path: 'enable'),
  //   2. disabling a parent while a dependent remains (path: 'disable'),
  //   3. pre-existing orphans in `current` that the delta doesn't fix
  //      (path: 'current') — without (3) the function could return
  //      `ok: true` and let callers rewrite a row that still violates
  //      the documented invariant. See the "preserves a pre-existing
  //      orphan" regression test.
  // We iterate `MCP_FLAG_VALUES` rather than the Set so the failure
  // order is deterministic (catalogue order).
  for (const flag of MCP_FLAG_VALUES) {
    if (!nextMcpSet.has(flag)) continue;
    const parents = MCP_FLAG_DEPENDENCIES[flag];
    for (const parent of parents) {
      if (nextMcpSet.has(parent)) continue;
      // Pick the path that best explains *why* this orphan exists, in
      // priority order: a user enable that introduced the orphan, a
      // user disable that removed the parent, or a pre-existing orphan
      // in the stored row.
      let path: 'enable' | 'disable' | 'current';
      if (enableResult.valid.includes(flag)) {
        path = 'enable';
      } else if (disableResult.valid.includes(parent)) {
        path = 'disable';
      } else {
        path = 'current';
      }
      dependencyFailures.push({
        kind: 'missing-parent',
        path,
        flag,
        requires: parent,
      });
    }
  }

  if (dependencyFailures.length > 0) {
    return { ok: false, failures: dependencyFailures };
  }

  const orderedMcp: McpFlag[] = MCP_FLAG_VALUES.filter((f) => nextMcpSet.has(f));
  return { ok: true, value: [...nonMcp, ...orderedMcp] };
}

function formatOne(failure: McpFlagFailure): string {
  switch (failure.kind) {
    case 'unknown-flag':
      return `Invalid flag "${String(failure.value)}" in ${failure.field}. Valid flags: ${MCP_FLAG_VALUES.join(', ')}`;
    case 'missing-parent':
      switch (failure.path) {
        case 'enable':
          return `Cannot enable ${failure.flag} without ${failure.requires}`;
        case 'disable':
          return `Cannot disable ${failure.requires}: ${failure.flag} would be orphaned`;
        case 'current':
          return `Stored available_features contains an orphaned ${failure.flag} (missing parent ${failure.requires}); refusing to overwrite`;
      }
    case 'conflicting-flag':
      return `Cannot enable and disable ${failure.flag} in the same request`;
    case 'invalid-current-set':
      return `Stored available_features is not an array (got ${
        typeof failure.value === 'object' ? (failure.value === null ? 'null' : 'object') : typeof failure.value
      }); refusing to overwrite`;
  }
}

export function formatMcpFlagFailures(failures: readonly McpFlagFailure[]): string {
  return failures.map(formatOne).join('; ');
}
